import { SerializedError } from '@databases/types';
import { Database } from '@databases/types/database';
import { isEqual, pickBy } from 'lodash-es';
import {
  deserializeError as deserializeErrorInternal,
  serializeError as serializeErrorInternal,
} from 'serialize-error';

import FileShim from './shims/file';

export { v4 as generateId } from 'uuid';

export let APP_MODE: 'STANDALONE' | 'EMBED' = 'STANDALONE';

export const setAppMode = (mode: typeof APP_MODE): void => {
  APP_MODE = mode;
};

export const checkAppMode = (): typeof APP_MODE => {
  // eslint-disable-next-line no-restricted-globals
  const urlSearchParams = new URLSearchParams(window.location.search);

  const isEmbedded =
    urlSearchParams.has('embedded') ||
    urlSearchParams.get('parentProduct') === 'smartLink';

  if (isEmbedded) {
    return (APP_MODE = 'EMBED');
  }

  return 'STANDALONE';
};

export function isStandalone(): boolean {
  return APP_MODE === 'STANDALONE';
}

export function isEmbed(): boolean {
  return APP_MODE === 'EMBED';
}

/**
 * Escape a string to be safely used inside HTML.
 */
export function encodeHtml(str: unknown) {
  if (typeof str !== 'string') {
    return '';
  }

  return `${str}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function encodeCdata(str: any): string {
  return `<![CDATA[${str.split(']]>').join(']]]]><![CDATA[>')}]]>`;
}

/**
 * Encodes a string to be used inside an CSS url() statement (without quotes).
 */
export function encodeCssUrl(str: string): string {
  return str.replace(/['"()\\\s]/g, (m) => `\\${m[0]}`);
}

/**
 * Removes line breaks and indentation from the given CSS code.
 */
export function trimCss(styles: string): string {
  return styles.trim().replace(/\s*\n\s*/g, ' ');
}

export function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
}

/**
 * Takes an async generator (generated by an async function*) and returns a promise that resolves to an array
 * with all the values.
 */
export async function asyncGeneratorToArray<T>(
  generator: AsyncGenerator<T, void, void>
): Promise<T[]> {
  const result: T[] = [];
  for await (const it of generator) {
    result.push(it);
  }
  return result;
}

/**
 * Converts an error object to a serializable plain object.
 */
export function serializeError(error: Error): SerializedError {
  return serializeErrorInternal(error) as any;
}

interface DatabaseError extends Error {
  /**
   * Indicates that a database entries CCEO, rather than a database CCEO was tried to be converted to a database.
   * The property will contain the content ID of the corresponding database CCEO.
   */
  _orderlyDatabaseContentId?: string;
}

/**
 * Converts an error object serialized by serializeError() back to a real Error object.
 */
export function deserializeError(error: SerializedError): DatabaseError {
  return deserializeErrorInternal(error);
}

/**
 * Wraps a function to handle its calls in batches. Returns a function that can be called with a single item as a parameter.
 * The given callback will be called after a small delay, with an array of items of all the function calls that have been
 * made in the meantime.
 * @param callback This callback will be called with an array of the items of all the function calls that have been made
 *                 since the callback was last called. The callback can return an array of results that must be in the same
 *                 order as the array of items that it received. Each function call will return its corresponding result
 *                 from the result array.
 * @param delay The number of milliseconds that the callback will be delayed for. By default, this is 0, meaning that
 *              only function calls from the same tick will be grouped together.
 */
export function getThrottledBatchCallback<Args extends any[]>(
  callback: (batch: Args[]) => void | Promise<void>,
  delay?: number
): (...args: Args) => Promise<void>;
export function getThrottledBatchCallback<Args extends any[], Result>(
  callback: (batch: Args[]) => Result[] | Promise<Result[]>,
  delay?: number
): (...args: Args) => Promise<Result>;
export function getThrottledBatchCallback<Args extends any[]>(
  callback: (batch: Args[]) => any[] | Promise<any[]>,
  delay?: number
): (...args: Args) => Promise<any>;
export function getThrottledBatchCallback<Args extends any[]>(
  callback: (batch: Args[]) => any,
  delay = 0
): (...args: Args) => Promise<any> {
  let items: Args[] = [];
  let promise: Promise<any> | undefined;

  return async (...args) => {
    if (!promise) {
      promise = (async () => {
        await sleep(delay);

        const batch = items;
        items = [];
        promise = undefined;
        return await callback(batch);
      })();
    }

    const idx = items.length;
    items.push(args);
    const results = await promise;
    return results?.[idx];
  };
}

/**
 * Returns true if the given object is a File object, even if the object comes from a different realm (frame).
 */
export function isFile(file: any): file is File {
  // The File constructor is not available in the Node.js backend
  return `${file}` === '[object File]' || file instanceof FileShim;
}

/**
 * Returns true if the given object is a Blob (or a File, which is a sub-type of Blob), even if the object comes
 * from a different realm (frame).
 */
export function isBlob(blob: any): blob is Blob {
  return `${blob}` === '[object Blob]' || isFile(blob);
}

/**
 * Returns true if the two arrays of string are equal ignoring the order of their entries.
 */
export function isEqualStringSet(set1: string[], set2: string[]): boolean {
  return isEqual([...set1].sort(), [...set2].sort());
}

/**
 * Returns true if the given colour is bright, meaning that if it is used as a background colour, a dark foreground
 * colour should be used for contrast.
 * @param colour The colour in hex format, for example #aaccdd or aaccdd
 * @param threshold The threshold from where the color will be bright
 */
export function isBright(colour: string, threshold = 0.5): boolean {
  const c = colour.replace(/^#/, '');
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  // See http://stackoverflow.com/a/596243/242365
  const brightness = Math.sqrt(0.241 * r * r + 0.691 * g * g + 0.068 * b * b);
  return brightness > threshold;
}

let lastObjectId = 1;
const objectIds = new WeakMap<any, number>();

/**
 * Returns a number that is unique to the given JavaScript object.
 */
export function getUniqueObjectId(obj: any): number {
  if (!objectIds.has(obj)) {
    objectIds.set(obj, lastObjectId++);
  }
  return objectIds.get(obj)!;
}

/**
 * Returns a wrapped version of the given callback that ensures a maximum number of concurrent calls. If the wrapped
 * callback is called while the specified maximum number of callbacks are already running, the call is delayed until
 * one of the running callbacks finishes.
 */
export function throttleConcurrency<Args extends any[], Result>(
  callback: (...args: Args) => Promise<Result>,
  concurrency: number
): (...args: Args) => Promise<Result> {
  let running = 0;
  const stack: Array<() => void> = [];
  return async (...args) => {
    if (running >= concurrency) {
      await new Promise<void>((resolve) => {
        stack.push(resolve);
      });
    }

    running++;
    try {
      return await callback(...args);
    } finally {
      running--;
      stack.shift()?.();
    }
  };
}

/**
 * Compares objects omitting properties which has value 'undefined.'
 */
export function isEqualOmitUndefined<T>(value: T, other: T): boolean {
  return isEqual(pickBy(value as any, undefined), pickBy(other as any, undefined));
}

export function isValidHyperlinkUrl(url: string): boolean {
  try {
    new URL(url);
    const urlPattern = /^(https?:\/\/)/;
    const mailtoPattern = /^mailto:(.+)/;
    if (urlPattern.test(url)) {
      return true;
    } else if (mailtoPattern.test(url)) {
      const matchResult = url.match(mailtoPattern);
      if (matchResult) {
        const [, email] = matchResult;
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailPattern.test(email);
      }
    }
    return false;
  } catch (e: unknown) {
    return false;
  }
}

/**
 * Returns the label that the UI should show if no label could be resolved for a hyperlink (yet), for example
 * because the resolution is still pending or a log in is required to load the actual destination page. Usually
 * the fallback will be the URL itself, but there may be some cases where a better label can be extracted from
 * the URL.
 */
export function getFallbackHyperlinkLabel(url: string): string {
  if (!isValidHyperlinkUrl(url)) {
    return url;
  }

  const parsed = new URL(url);
  let m;
  if ((m = parsed.pathname.match(/\/browse\/([A-Z]+-[0-9]+)$/))) {
    // Jira issue key
    return m[1];
  } else {
    return url;
  }
}

/**
 * It trims the text and replaces multiple white spaces with a single white space
 * Also, it converts the text to lowercase
 * If the input is undefined, it returns an empty string.
 */
export const normalizeText = (text: string | undefined) =>
  text?.trim().replace(/\s+/, ' ').toLowerCase() || '';

export const getValueId = (entryId: string, fieldId: string) => `${entryId}-${fieldId}`;

export const isValidValueId = (valueId: string) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(valueId);
};

export const getEntryIdFromValueId = (valueId: string) => {
  if (!isValidValueId(valueId)) {
    return null;
  }
  const parts = valueId.split('-');
  const entryId = parts.slice(0, 5).join('-');
  return entryId;
};

export const getFieldIdFromValueId = (valueId: string) => {
  if (!isValidValueId(valueId)) {
    return null;
  }
  const parts = valueId.split('-');
  const fieldId = parts.slice(5).join('-');
  return fieldId;
};

export const threadsForField = (database: Database, fieldId: string) => {
  return Object.keys(database.commentThreads)
    .filter((valueId) => getFieldIdFromValueId(valueId) === fieldId)
    .map((valueId) => database.commentThreads[valueId]);
};

export const fieldHasThreads = (database: Database, fieldId: string) => {
  return threadsForField(database, fieldId).length > 0;
};

/**
 * Given an threadId and comments object, it will
 * return the associated thread object as well as
 * it's entryId and fieldId.
 */
export const getThreadObjectFromId = (
  threadId: string | undefined,
  comments: Record<
    string,
    {
      reactionsId: string | undefined;
      threadId: string;
      isResolved: boolean;
      commentCount: number;
      versionNumber: number;
    }[]
  >
) => {
  if (threadId) {
    for (const [valueId, threads] of Object.entries(comments)) {
      return {
        thread: threads.find((thread) => thread.threadId === threadId),
        entryId: getEntryIdFromValueId(valueId),
        fieldId: getFieldIdFromValueId(valueId),
      };
    }
  }
};
